'use client';

import { Check, Copy } from 'lucide-react';

import { formatTokens } from '@/components/ui/context-map-clean';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';

import type { UsageTokens } from '../utils/usageChip';

const STATS: {
  key: keyof UsageTokens;
  label: string;
  title: string;
}[] = [
  {
    key: 'input',
    label: 'Input',
    title: 'What Connie read fresh this turn',
  },
  {
    key: 'cacheRead',
    label: 'From cache',
    title: 'What Connie reused from earlier, at a fraction of the cost',
  },
  {
    key: 'cacheWrite',
    label: 'Saved to cache',
    title: 'What Connie kept so the next question costs less',
  },
  {
    key: 'output',
    label: 'Output',
    title: 'What Connie wrote, tool calls included',
  },
];

/**
 * A turn's token counts and session id, in the Context Map's compaction-line style
 * (FSI #1224). Sits under the map, or alone under a Usage row without one. Users
 * send the session id to support, so it is always shown with a copy button.
 */
export const UsageLine = ({
  tokens,
  sessionId,
}: {
  tokens?: UsageTokens;
  sessionId?: string;
}) => {
  const { isCopied, copyToClipboard } = useCopyToClipboard();
  if (!tokens && !sessionId) return null;

  return (
    <div className='connie-usage-line' data-cy='usage-line'>
      {tokens ? (
        <span className='connie-usage-line__stats'>
          {STATS.map(stat => (
            <span
              key={stat.key}
              className='connie-usage-line__stat'
              title={`${stat.title}: ${tokens[stat.key].toLocaleString()} tokens`}
              data-cy={`usage-${stat.key}`}
            >
              {stat.label} <b>{formatTokens(tokens[stat.key])}</b>
            </span>
          ))}
        </span>
      ) : null}
      {sessionId ? (
        <span className='connie-usage-line__session'>
          <span className='shrink-0'>Session</span>
          <span
            data-cy='usage-session-id'
            className='connie-usage-line__id'
            title={sessionId}
          >
            {sessionId}
          </span>
          <button
            type='button'
            data-cy='usage-session-copy'
            aria-label={isCopied ? 'Session id copied' : 'Copy session id'}
            title={isCopied ? 'Copied' : 'Copy session id'}
            onClick={() => copyToClipboard(sessionId)}
            className='connie-usage-line__copy'
          >
            {isCopied ? (
              <Check size={12} className='text-green-400' />
            ) : (
              <Copy size={12} />
            )}
          </button>
        </span>
      ) : null}
    </div>
  );
};
